import { useState, useEffect, useCallback } from 'react';
import { creditScoreApi, type CreditScoreStats } from '../services/api/endpoints/creditScore';

/**
 * Fetches and caches credit-score stats for a list of company IDs.
 * Returns a map { [companyId]: CreditScoreStats | null } and a refresh function.
 */
export function useCreditScores(companyIds: string[]) {
  const [scoreMap, setScoreMap] = useState<Record<string, CreditScoreStats | null>>({});

  const fetchOne = useCallback(async (companyId: string) => {
    const result = await creditScoreApi.getStats(companyId);
    if (result.success && result.data) {
      setScoreMap((prev) => ({ ...prev, [companyId]: result.data!.stats }));
    } else {
      setScoreMap((prev) => ({ ...prev, [companyId]: null }));
    }
  }, []);

  // Fetch all on mount / when IDs change
  useEffect(() => {
    if (companyIds.length === 0) return;
    companyIds.forEach(fetchOne);
  }, [companyIds, fetchOne]);

  /** Refresh a single company's score (e.g. after CET save or recalculation) */
  const refreshScore = useCallback((companyId: string) => {
    fetchOne(companyId);
  }, [fetchOne]);

  return { scoreMap, refreshScore };
}
